import React, { useMemo } from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  Box,
  Paper,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import dayjs from "dayjs";
import type { UnifiedEvent } from "../../../types/events";
import { EVENT_COLORS, ALERT_STATUS_COLORS } from "../../../constants/eventColors";
import {
  isAlertEvent,
  isJourneyEvent,
  isForbiddenZoneEvent,
} from "../../../utils/unifiedEvents";

interface UnifiedTimelineProps {
  events: UnifiedEvent[];
  maxItems?: number;
}

/** Solid status pill — same style as bandeau cards */
function StatusChip({ event, translate }: { event: UnifiedEvent; translate: any }) {
  let label = "";
  let bgColor = "";

  if (isAlertEvent(event)) {
    bgColor = ALERT_STATUS_COLORS[event.data.status];
    label = translate(`monitoring.status.${event.data.status.toLowerCase()}`, event.data.status);
  } else if (isJourneyEvent(event)) {
    const ended = event.data.hasEnded;
    bgColor = ended ? "#4CAF50" : "#2196F3";
    label = ended ? translate("monitoring.ended", "Ended") : translate("monitoring.active", "Active");
  } else if (isForbiddenZoneEvent(event)) {
    const exited = !!event.data.exitedAt;
    bgColor = exited ? "#4CAF50" : "#FF9800";
    label = exited ? translate("monitoring.exited", "Exited") : translate("monitoring.stillInside", "Still inside");
  } else {
    return null;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.6rem",
        height: "22px",
        flexShrink: 0,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

export const UnifiedTimeline: React.FC<UnifiedTimelineProps> = ({
  events,
  maxItems = 20,
}) => {
  const translate = useTranslate();
  const { show } = useNavigation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const sortedEvents = useMemo(() => {
    return [...events]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, maxItems);
  }, [events, maxItems]);

  if (sortedEvents.length === 0) {
    return (
      <Paper sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2, borderRadius: "12px" }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {translate("monitoring.noEndedEvents", "No recent ended events")}
        </Typography>
      </Paper>
    );
  }

  const typeLabel = (event: UnifiedEvent) =>
    event.eventType === "alert"
      ? translate("monitoring.alert", "Alert")
      : event.eventType === "journey"
        ? translate("monitoring.journey", "Journey")
        : translate("monitoring.forbiddenZoneFull", "Zone interdite");

  return (
    <Paper sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ p: { xs: 1, md: 2 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant={isSmallScreen ? "subtitle1" : "h6"} sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}>
          {translate("monitoring.recentHistory", "Recent History")}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", minHeight: { xs: "250px", md: "400px" } }}>
        <Timeline position="right" sx={{ p: 0, m: 0 }}>
          {sortedEvents.map((event, index) => {
            const colors = EVENT_COLORS[event.eventType];

            return (
              <TimelineItem
                key={event.key}
                sx={{
                  // Center the dot vertically relative to the card
                  "&::before": { display: "none" },
                  alignItems: "center",
                  minHeight: 0,
                }}
              >
                <TimelineOppositeContent
                  sx={{
                    flex: "0 0 auto",
                    width: { xs: "50px", md: "60px" },
                    py: { xs: 0.75, md: 1 },
                    px: { xs: 0.5, md: 1 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", md: "0.75rem" }, lineHeight: 1.3 }}>
                    {dayjs(event.timestamp).format("DD/MM")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", md: "0.75rem" }, lineHeight: 1.3 }}>
                    {dayjs(event.timestamp).format("HH:mm")}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator sx={{ alignSelf: "stretch" }}>
                  {/* Top connector — fills space above the dot */}
                  {index > 0 ? (
                    <TimelineConnector sx={{ backgroundColor: theme.palette.divider, flex: 1 }} />
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                  <TimelineDot sx={{ backgroundColor: colors.main, m: 0, flexShrink: 0 }} />
                  {/* Bottom connector — fills space below the dot */}
                  {index < sortedEvents.length - 1 ? (
                    <TimelineConnector sx={{ backgroundColor: theme.palette.divider, flex: 1 }} />
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                </TimelineSeparator>

                <TimelineContent sx={{ py: { xs: 0.75, md: 1 }, px: { xs: 0.5, md: 1 } }}>
                  <Box
                    onClick={() => handleEventClick(event)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      p: { xs: 1, md: 1.5 },
                      // Extra bottom padding to make room for the type badge
                      pb: { xs: 3, md: 3.5 },
                      borderRadius: "12px",
                      border: `2px solid ${colors.main}`,
                      backgroundColor: isDark ? alpha(colors.main, 0.08) : alpha(colors.main, 0.04),
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: isDark ? alpha(colors.main, 0.16) : alpha(colors.main, 0.1),
                      },
                    }}
                  >
                    {/* Header: user name + status chip top-right */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.8rem", md: "0.875rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {event.userName || event.data?.user?.email || "-"}
                      </Typography>
                      <StatusChip event={event} translate={translate} />
                    </Box>

                    {/* Extra details for forbidden zones */}
                    {isForbiddenZoneEvent(event) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontWeight: 500 }}>
                        {event.data.place?.emoji} {event.data.place?.name}
                      </Typography>
                    )}

                    {/* Type badge — fused bottom-left */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        backgroundColor: colors.main,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.6rem",
                        lineHeight: 1,
                        px: 1,
                        pt: "6px",
                        pb: "4px",
                        borderTopRightRadius: "10px",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      {typeLabel(event)}
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>
    </Paper>
  );

  function handleEventClick(event: UnifiedEvent) {
    if (isAlertEvent(event)) {
      if (event.data.isBroadcasting) {
        show("monitoring", event.data.id);
      } else {
        window.open(`/history/alert/${event.data.id}`, "_blank");
      }
    } else if (isJourneyEvent(event)) {
      window.open(`/history/journey/${event.data.id}`, "_blank");
    } else if (isForbiddenZoneEvent(event)) {
      window.open(`/history/forbidden-zone/${event.data.id}`, "_blank");
    }
  }
};
